/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Row, Space, Tooltip, Typography } from 'antd';
import ActivityLink from './ActivityLink';

// Misc imports
import { truncate } from '../../utils/string/truncate';
import { VARIABLE_TYPES } from '../../constants/main';

const { Paragraph, Text } = Typography;

/**
 * The following component is used to display a variable in a Select or Menu component.
 * It displays the variable name and description, and if the variable is from an activity,
 * it also displays the activity name with a link.
 *
 * @param {Object} props
 * @param {Object} props.variable The variable object
 * @param {Number} props.truncateCount The number of characters to truncate the variable name and description to
 *
 * @returns {React.Component} The variable select item component
 */
const VariableSelectItem = ({ variable, botId, truncateCount, truncateDescriptionCount, showDescription, showActivity = true }) => {
  const { t } = useTranslation();

  // Determine if each section should be shown in the tooltip.
  const showValue = variable.type === VARIABLE_TYPES.STATIC && variable.value;
  const showName = variable.name && truncateCount && variable.name.length > truncateCount;
  const showDesc = showDescription && variable.description && truncateDescriptionCount && variable.description.length > truncateDescriptionCount;

  // Build the tooltip sections based on the conditions.
  const tooltipSections = [];
  if (showValue) {
    tooltipSections.push(
      <span key="value">
        <Paragraph className="mb-0" strong type="secondary">
          {t('Variable value')}:
        </Paragraph>
        <Paragraph type="secondary" className="mb-0">
          {variable.value}
        </Paragraph>
      </span>
    );
  }
  if (showName) {
    tooltipSections.push(
      <span key="name">
        <Paragraph className="mb-0" strong type="secondary">
          {t('Variable name')}:
        </Paragraph>
        <Paragraph type="secondary" className="mb-0">
          {variable.name}
        </Paragraph>
      </span>
    );
  }
  if (showDesc) {
    tooltipSections.push(
      <span key="description">
        <Paragraph className="mb-0" strong type="secondary">
          {t('Description')}:
        </Paragraph>
        <Paragraph type="secondary" className="mb-0">
          {variable.description}
        </Paragraph>
      </span>
    );
  }

  // If no section qualifies, then the tooltip title is null.
  const tooltipTitle = tooltipSections.length > 0 ? <Space direction="vertical">{tooltipSections}</Space> : null;

  return (
    <Fragment>
      <Row className="flex-row">
        <Col xs={24}>
          {/* Wrap the main content with a single Tooltip */}
          <Tooltip placement="left" mouseEnterDelay={0.5} title={tooltipTitle}>
            <Row gutter={6} wrap="nowrap">
              <Col flex="auto" xs={showActivity ? 18 : 24}>
                <Text ellipsis>
                  {truncateCount ? truncate(variable.name, truncateCount, '...') : variable.name}
                </Text>
              </Col>
              <Col xs={showActivity ? 6 : 0}>
                <Space className="float-right">
                  {showActivity && (
                    <ActivityLink
                      botId={botId}
                      activityId={variable.fromActivity}
                      fetchChaptersourcesIndividualy={false}
                      showIcon={false}
                      truncateCount={14}
                      notFoundText={null}
                    />
                  )}
                </Space>
              </Col>
            </Row>
          </Tooltip>
        </Col>

        {showDescription && (
          <Col xs={24}>
            {/* The description below is rendered normally (truncated) without its own tooltip */}
            <Text type="secondary fs-sm">
              {variable.description
                ? truncateDescriptionCount
                  ? truncate(variable.description, truncateDescriptionCount, '...')
                  : variable.description
                : null}
            </Text>
          </Col>
        )}
      </Row>
    </Fragment>
  );
};

VariableSelectItem.propTypes = {
  variable: PropTypes.object,
  truncateCount: PropTypes.number,
  truncateDescriptionCount: PropTypes.number,
  showDescription: PropTypes.bool,
  showActivity: PropTypes.bool,
  botId: PropTypes.string
};

export default VariableSelectItem;
